import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Clock,
  Bell,
  Shield,
  Palette,
  Database,
  Users,
  Briefcase,
  Settings as SettingsIcon,
} from "lucide-react";
import { ShiftsTab } from "./ShiftsTab";
import { PositionsTab } from "./PositionsTab";
import { BrandingTab } from "./BrandingTab";

type SettingsTab =
  | "shifts"
  | "positions"
  | "branding"
  | "notifications"
  | "security"
  | "appearance"
  | "integrations"
  | "team";

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: "shifts",
    label: "Shifts",
    icon: Clock,
    description: "Configure employee shifts",
  },
  {
    id: "positions",
    label: "Positions",
    icon: Briefcase,
    description: "Manage employee positions",
  },
  {
    id: "branding",
    label: "Branding",
    icon: Palette,
    description: "Logo and brand colors",
  },
  // Future tabs - placeholder comments for expansion
  // {
  //   id: "notifications",
  //   label: "Notifications",
  //   icon: Bell,
  //   description: "Configure notification preferences",
  // },
  // {
  //   id: "security",
  //   label: "Security",
  //   icon: Shield,
  //   description: "Security and privacy settings",
  // },
  // {
  //   id: "appearance",
  //   label: "Appearance",
  //   icon: Palette,
  //   description: "Customize the look and feel",
  // },
  // {
  //   id: "integrations",
  //   label: "Integrations",
  //   icon: Database,
  //   description: "Connect third-party services",
  // },
  // {
  //   id: "team",
  //   label: "Team Settings",
  //   icon: Users,
  //   description: "Manage team-wide settings",
  // },
];

export default function GeneralSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>("shifts");

  // Initialize tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam &&
      (tabParam === "shifts" || tabParam === "positions" || tabParam === "branding")
    ) {
      setActiveTab(tabParam as SettingsTab);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "shifts":
        return <ShiftsTab />;
      case "positions":
        return <PositionsTab />;
      case "branding":
        return <BrandingTab />;
      // Future tab content - placeholder comments
      // case "notifications":
      //   return <NotificationsTab />;
      // case "security":
      //   return <SecurityTab />;
      // case "appearance":
      //   return <AppearanceTab />;
      // case "integrations":
      //   return <IntegrationsTab />;
      // case "team":
      //   return <TeamSettingsTab />;
      default:
        return <ShiftsTab />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">General Settings</h1>
            <p className="mt-1 text-gray-500">
              Manage your organization's settings and preferences
            </p>
          </div>
        </div>

        {/* Settings Layout with Vertical Tabs */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Left Sidebar - Vertical Tab Navigation */}
          <div className="lg:col-span-1">
            <Card className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <Button
                      key={tab.id}
                      variant="ghost"
                      className={cn(
                        "h-auto w-full justify-start gap-3 px-3 py-3",
                        isActive && "bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                      )}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 flex-shrink-0",
                          isActive ? "text-blue-700" : "text-gray-500"
                        )}
                      />
                      <div className="flex flex-col items-start text-left">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            isActive ? "text-blue-700" : "text-gray-900"
                          )}
                        >
                          {tab.label}
                        </span>
                        <span
                          className={cn(
                            "mt-0.5 text-xs",
                            isActive ? "text-blue-600" : "text-gray-500"
                          )}
                        >
                          {tab.description}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </nav>
            </Card>

            {/* Future Tabs Section - Placeholder */}
            {/* Uncomment when adding more tabs */}
            {/* <Card className="p-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <SettingsIcon className="h-4 w-4" />
                <span>More settings coming soon</span>
              </div>
            </Card> */}
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-3">{renderTabContent()}</div>
        </div>
      </div>
    </DashboardLayout>
  );
}
